import { useState,useCallback } from 'react'
import { api } from '@/lib/api-client'
import { Badge } from '@/components/ui/badge'
import { HoverCard,HoverCardContent,HoverCardTrigger } from '@/components/ui/hover-card'
import { Shield,Loader2,ShieldAlert,ShieldCheck,Clock,User,Ban } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import { Separator } from '@/components/ui/separator'

interface AuditEvent {
  id: string
  action: string
  reason?: string | null
  note?: string | null
  performedAt: string
  performedByUserId: string
  performedByUserName?: string
  user?: { id: string; name?: string; email?: string } | null
  createdAt?: string
}

interface SuspensionAuditHoverCardProps {
  id: string
  type: 'product' | 'sale-config' | 'inventory'
  label?: string
}

function isPositiveAction(action: string) {
  const a = action.toLowerCase()
  return a === 'resumed' || a === 'released'
}

function getActionLabel(action: string) {
  const a = action.toLowerCase()
  if (a === 'suspended') return 'Suspended'
  if (a === 'resumed') return 'Resumed'
  if (a === 'placed') return 'Hold Placed'
  if (a === 'released') return 'Hold Released'
  return action
}

export function SuspensionAuditHoverCard({ id, type, label }: SuspensionAuditHoverCardProps) {
  const [events, setEvents] = useState<AuditEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [fetched, setFetched] = useState(false)

  const fetchHistory = useCallback(async () => {
    if (fetched) return
    setLoading(true)
    try {
      let url = ''
      if (type === 'product') url = `/products/${id}/suspension-history`
      else if (type === 'sale-config') url = `/sale-configs/${id}/suspension-history`
      else if (type === 'inventory') url = `/inventory/${id}/hold/history`

      const res = await api.get<{ data?: unknown; items?: unknown }>(url, { params: { limit: 10 } })
      const rawEvents: unknown = res?.data ?? res?.items ?? res ?? []
      setEvents(Array.isArray(rawEvents) ? (rawEvents as AuditEvent[]) : [])
      setFetched(true)
    } catch (err: unknown) {
      console.error(`Failed to fetch ${type} history:`, err)
    } finally {
      setLoading(false)
    }
  }, [id, type, fetched])

  const defaultLabel = type === 'inventory' ? 'Hold History' : 'Suspension History'

  return (
    <HoverCard openDelay={300} closeDelay={100} onOpenChange={(open) => { if (open) void fetchHistory() }}>
      <HoverCardTrigger asChild>
        <Badge
          variant="secondary"
          className={cn(
            'text-[10px] h-5 px-1.5 font-normal gap-1 cursor-help',
            type === 'inventory'
              ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400 border-amber-500/20'
              : 'bg-red-500/10 text-red-600 dark:text-red-400 border-red-500/20',
          )}
        >
          {type === 'inventory' ? <Shield className="w-3 h-3" /> : <Ban className="w-3 h-3" />}
          {label ?? (type === 'inventory' ? 'On Hold' : 'Suspended')}
        </Badge>
      </HoverCardTrigger>
      <HoverCardContent className="w-80 p-0" align="start" side="bottom">
        <div className="px-3 pt-3 pb-2 flex items-center gap-2">
          <div
            className={cn(
              'flex items-center justify-center h-6 w-6 rounded-md',
              type === 'inventory' ? 'bg-amber-500/10' : 'bg-red-500/10',
            )}
          >
            {type === 'inventory' ? (
              <Shield className="h-3.5 w-3.5 text-amber-600" />
            ) : (
              <Ban className="h-3.5 w-3.5 text-red-600" />
            )}
          </div>
          <div>
            <p className="text-xs font-semibold text-foreground leading-none">{defaultLabel}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Audit trail for this {type}</p>
          </div>
        </div>
        <Separator />

        {loading && !fetched ? (
          <div className="flex flex-col items-center gap-1.5 py-6 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-[11px]">Loading history…</span>
          </div>
        ) : events.length === 0 ? (
          <div className="flex flex-col items-center gap-1.5 py-6 text-muted-foreground">
            <Ban className="w-4 h-4 opacity-40" />
            <span className="text-[11px]">No events recorded</span>
          </div>
        ) : (
          <div className="max-h-[260px] overflow-y-auto">
            {events.map((evt, idx) => {
              const positive = isPositiveAction(evt.action)
              const timestamp = evt.performedAt ?? evt.createdAt
              return (
                <div key={evt.id ?? idx}>
                  <div className="px-3 py-2 flex gap-2.5">
                    <div
                      className={cn(
                        'mt-0.5 flex items-center justify-center h-5 w-5 rounded-full shrink-0',
                        !positive
                          ? 'bg-red-500/10 text-red-600 dark:text-red-400'
                          : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
                      )}
                    >
                      {!positive ? <ShieldAlert className="h-3 w-3" /> : <ShieldCheck className="h-3 w-3" />}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span
                          className={cn(
                            'text-[11px] font-medium',
                            !positive
                              ? 'text-red-600 dark:text-red-400'
                              : 'text-emerald-600 dark:text-emerald-400',
                          )}
                        >
                          {getActionLabel(evt.action)}
                        </span>
                        {timestamp && (
                          <span className="text-[10px] text-muted-foreground shrink-0 flex items-center gap-1">
                            <Clock className="h-2.5 w-2.5" />
                            {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
                          </span>
                        )}
                      </div>

                      {(evt.reason ?? evt.note) && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 leading-snug line-clamp-2">
                          {evt.reason ?? evt.note}
                        </p>
                      )}

                      {(evt.user?.name ?? evt.performedByUserName) && (
                        <div className="flex items-center gap-1 mt-1 text-[10px] text-muted-foreground/60">
                          <User className="h-2.5 w-2.5" />
                          <span>{evt.user?.name ?? evt.performedByUserName}</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {idx < events.length - 1 && <Separator />}
                </div>
              )
            })}
          </div>
        )}
      </HoverCardContent>
    </HoverCard>
  )
}
